import {
  Badge,
  Divider,
  Flex,
  Grid,
  Label,
  Loader,
} from "@aws-amplify/ui-react";
import {
  CCard,
  CModal,
  CModalBody,
  CModalHeader,
  CModalTitle,
} from "@coreui/react";
import { API, graphqlOperation, Storage } from "aws-amplify";
import { useContext, useEffect, useState } from "react";
import LocalizedStrings from "react-localization";
import { Add, Close } from "@mui/icons-material";

import { getUser, listUsers } from "graphql/queries";
import ActionIcon from "components/actions/ActionIcon";
import DefaultPhoto from "static/images/DefaultPhoto.png";
import SelectUser from "../users/SelectUser";
import { OrderContext } from "./context/OrderContext";
import { useOnlineStatus } from "hooks/useOnlineStatus";
import { localGetServiceOrder } from "libs/idb/service-orders-db";

const ExecutorCard = ({
  executorID,
  selectUserExecutor,
  readOnly = false,
  hasAuxExecutors = false,
  auxExecutors,
  onAddAuxExecutor,
  onRemoveAuxExecutor,
  hidePhoto = false,
}) => {
  const online = useOnlineStatus();
  const { serviceOrder } = useContext(OrderContext);
  const [executorState, setExecutorState] = useState({});
  const [auxExecutorsState, setAuxExecutorsState] = useState({
    data: [],
    loading: false,
  });
  const [loading, setLoading] = useState(false);
  const [modalSelectExecutor, setModalSelectExecutor] = useState({
    visible: false,
  });

  const onSelectUser = (id) => {
    if (!id) return;
    if (!!modalSelectExecutor.addAux) onAddAuxExecutor(id);
    else selectUserExecutor(id);
    setModalSelectExecutor({ visible: false });
  };

  async function fetchUser(id) {
    try {
      setLoading(true);
      if (!online) {
        const localData = await localGetServiceOrder(serviceOrder.id);
        const userFound = localData?.users?.find((loc) => loc.id === id);
        setExecutorState(userFound);
        return;
      }
      const userData = await API.graphql(
        graphqlOperation(getUser, {
          id,
        })
      );
      const userItem = userData.data.getUser;
      if (userItem.photoSrc) {
        userItem.photoSrc = await Storage.get(
          `profile-photos/${userItem.photoSrc}`,
          {
            level: "public",
          }
        );
      }
      setExecutorState(userItem);
    } catch (err) {
      console.log("Error fetchUser", err);
    } finally {
      setLoading(false);
    }
  }

  async function fetchAuxExecutors(ids) {
    try {
      setAuxExecutorsState((prev) => ({ ...prev, loading: true }));
      if (!online) {
        const localData = await localGetServiceOrder(serviceOrder.id);
        const usersFound =
          localData?.users?.filter((loc) => ids?.some((id) => id === loc.id)) ??
          [];
        setAuxExecutorsState({ data: usersFound, loading: false });
        return;
      }
      const userData = await API.graphql(
        graphqlOperation(listUsers, {
          filter: {
            or: ids?.map((id) => ({ id: { eq: id } })) ?? [],
          },
        })
      );
      const usersItems = userData.data.listUsers.items;
      setAuxExecutorsState({ data: usersItems, loading: false });
    } catch (err) {
      console.log("Error fetchAuxExecutors", err);
    } finally {
      setAuxExecutorsState((prev) => ({ ...prev, loading: false }));
    }
  }

  useEffect(() => {
    if (executorID) fetchUser(executorID);
    else setExecutorState({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [executorID]);

  useEffect(() => {
    if (auxExecutors?.length > 0) fetchAuxExecutors(auxExecutors);
    else setAuxExecutorsState({ data: [], loading: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auxExecutors]);

  return (
    <>
      <CCard
        className="p-3"
        borderRadius="medium"
        variation="outlined"
        style={{ height: "100%" }}
      >
        {loading ? (
          <Loader />
        ) : (
          <Grid>
            <Flex gap="5px" justifyContent="center">
              {!hidePhoto && (
                <Grid gap="5px" style={{ marginRight: "auto" }}>
                  <img
                    src={executorState?.photoSrc || DefaultPhoto}
                    alt="photo_profile"
                    className="rounded"
                    style={{
                      maxWidth: "80px",
                      maxHeight: "80px",
                      alignSelf: "center",
                    }}
                    crossorigin="anonymous"
                  />
                  {!readOnly && (
                    <Flex justifyContent="center" gap="0px">
                      <ActionIcon
                        name={strings.editExecutor}
                        icon="Edit"
                        color="primary"
                        onClick={() =>
                          setModalSelectExecutor({ visible: true })
                        }
                      />
                      {executorState?.email && (
                        <ActionIcon
                          name={strings.deleteExecutor}
                          icon="Delete"
                          color="red.60"
                          onClick={() => selectUserExecutor(null)}
                        />
                      )}
                    </Flex>
                  )}
                </Grid>
              )}
              <Grid
                gap="5px"
                textAlign="center"
                alignContent="center"
                style={{ overflowWrap: "anywhere" }}
              >
                {!executorState?.email && strings.noExecutor}
                {executorState?.email && (
                  <>
                    <b>{strings.leaderExecutor}</b>
                    <span>{executorState?.name}</span>
                    <span>{executorState?.email}</span>
                    <span>{executorState?.position}</span>
                  </>
                )}
              </Grid>
            </Flex>
            {hasAuxExecutors &&
              (auxExecutorsState.data?.length > 0 || !readOnly) && (
                <>
                  <Divider className="my-2" size="small" />
                  <Label>{strings.auxExecutors}</Label>
                  <Flex className="mt-2" gap="7px" wrap="wrap">
                    {auxExecutorsState.loading ? (
                      <Loader />
                    ) : (
                      auxExecutorsState.data?.map((aux, index) => (
                        <Flex alignItems="center">
                          <Badge key={index}>
                            <span>{aux.name}</span>
                            {!readOnly && (
                              <Close
                                style={{ cursor: "pointer", marginLeft: "2px" }}
                                fontSize="inherit"
                                onClick={() => onRemoveAuxExecutor(aux.id)}
                              />
                            )}
                          </Badge>
                        </Flex>
                      ))
                    )}
                    {!readOnly && (
                      <CCard
                        className="p-2"
                        style={styles.addBox}
                        onClick={() =>
                          setModalSelectExecutor({
                            visible: true,
                            addAux: true,
                          })
                        }
                      >
                        <Add fontSize="small" color="action"></Add>
                        {strings.add}
                      </CCard>
                    )}
                  </Flex>
                </>
              )}
          </Grid>
        )}
      </CCard>
      {modalSelectExecutor.visible && (
        <CModal
          portal={false}
          alignment="center"
          visible={modalSelectExecutor.visible}
          backdrop="static"
          onClose={() => setModalSelectExecutor({ visible: false })}
        >
          <CModalHeader>
            <CModalTitle>{strings.selectExecutor}</CModalTitle>
          </CModalHeader>
          <CModalBody>
            <SelectUser onSelect={onSelectUser} />
          </CModalBody>
        </CModal>
      )}
    </>
  );
};

const strings = new LocalizedStrings({
  es: {
    leaderExecutor: "Líder Ejecutor",
    executor: "Ejecutor",
    auxExecutors: "Auxiliares",
    editExecutor: "Editar Ejecutor",
    selectExecutor: "Seleccionar Ejecutor",
    deleteExecutor: "Eliminar Ejecutor",
    noExecutor: "No se ha seleccionado un ejecutor",
  },
});

const styles = {
  addBox: {
    cursor: "pointer",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
  },
};

export default ExecutorCard;
